// @ts-nocheck
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CalendarCheckIcon } from '@pleio/icons/calendar-check'
import { CalendarPlusIcon } from '@pleio/icons/calendar-plus'
import { CalendarWaitingIcon } from '@pleio/icons/calendar-waiting'
import { OptionsIcon } from '@pleio/icons/options'

import DropdownButton from 'js/components/DropdownButton'
import CustomFormModal from 'js/components/Form/CustomFields/CustomFormModal'
import IconButton from 'js/components/IconButton'
import CopyItem from 'js/components/Item/ItemActions/components/CopyItem'
import DeleteForm from 'js/components/Item/ItemActions/components/DeleteForm'
import Modal from 'js/components/Modal/Modal'
import { Tooltip } from 'js/components/Tooltip/Tooltip'
import JoinQueueModal from 'js/events/EventAttend/components/JoinQueueModal'
import LeaveGuestListModal from 'js/events/EventAttend/components/LeaveGuestListModal'
import LeaveQueueModal from 'js/events/EventAttend/components/LeaveQueueModal'
import useEventActions from 'js/events/hooks/useEventActions'

import type { EventChildFragmentFragment } from '../EventActivities/EventChildFragment.fragment.generated'
import type { EventItemQuery } from '../EventPage/EventItem.queries.generated'
import type { EventItemViewFragment } from '../EventPage/EventItemView.fragment.generated'

type Props = {
    entity:
        | EventChildFragmentFragment
        | NonNullable<NonNullable<EventItemViewFragment['children']>[number]>
    viewer: EventItemQuery['viewer']
    refetchQueries: any
    disabled: boolean
}

const ActivityActions = ({
    entity,
    viewer,
    refetchQueries,
    disabled,
}: Props) => {
    const { t } = useTranslation()

    const { loading, handleSubmit } = useEventActions(entity.guid)

    const [showCopyEventModal, setShowCopyEventModal] = useState(false)

    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const toggleDeleteModal = () => {
        setShowDeleteModal(!showDeleteModal)
    }

    const [showJoinQueueModal, setShowJoinQueueModal] = useState(false)
    const toggleJoinQueueModal = () => {
        setShowJoinQueueModal(!showJoinQueueModal)
    }

    const [showLeaveQueueModal, setShowLeaveQueueModal] = useState(false)
    const toggleLeaveQueueModal = () =>
        setShowLeaveQueueModal(!showLeaveQueueModal)

    const [showLeaveGuestListModal, setShowLeaveGuestListModal] =
        useState(false)
    const toggleShowLeaveGuestListModal = () =>
        setShowLeaveGuestListModal(!showLeaveGuestListModal)

    const [showAttendForm, setShowAttendForm] = useState(false)
    const toggleAttendForm = () => setShowAttendForm(!showAttendForm)

    if (!viewer.loggedIn) {
        return null
    }

    const {
        guid,
        title,
        group,
        canEdit,
        isAttending,
        maxAttendees,
        attendees: { totalAccept } = {},
        alreadySignedUpInSlot,
        rsvp,
        isFormEnabled,
        form,
    } = entity

    const options = canEdit
        ? [
              {
                  label: t('action.edit-item'),
                  'aria-label': t('action.edit-item-label', {
                      title,
                  }),
                  url: `${group?.url || ''}/events/edit/${guid}`,
              },
              {
                  label: t('action.duplicate-item'),
                  'aria-label': t('action.duplicate-item-label', {
                      title: entity.title,
                  }),
                  onClick: () => setShowCopyEventModal(true),
              },
              {
                  label: t('action.delete-item'),
                  'aria-label': t('action.delete-item-label', {
                      title,
                  }),
                  onClick: toggleDeleteModal,
              },
          ]
        : []

    const spotsLeft = maxAttendees - totalAccept
    const isFull = maxAttendees && spotsLeft < 1

    // TODO: Duplicate code present in EventAttend.jsx
    const signUp = () => {
        if (isFull) {
            toggleJoinQueueModal()
            return
        }
        if (isFormEnabled) {
            toggleAttendForm()
            return
        }
        handleSubmit({ state: 'accept' })
    }

    const joinQueue = (options) => {
        if (isFormEnabled) {
            toggleAttendForm()
            return
        }

        handleSubmit(options)
    }

    const signOut = () => {
        // Show warning if user is leaving the guest list (if it has max attendees or also attending sub events)
        if (isAttending === 'accept' && maxAttendees) {
            toggleShowLeaveGuestListModal()
            return
        }
        handleSubmit({ state: 'reject' })
    }

    const isGoing = ['accept', 'online'].includes(isAttending)

    let AttendButton = null
    if (isGoing) {
        AttendButton = (
            <IconButton
                variant="primary"
                size="large"
                tooltip={
                    isAttending === 'online'
                        ? t('entity-event.you-are-going-online')
                        : t('entity-event.you-are-going')
                }
                aria-label={t('entity-event.exit-guest-list')}
                onClick={signOut}
                loading={loading}
                tooltipProps={{ side: 'left' }}
            >
                <CalendarCheckIcon />
            </IconButton>
        )
    } else if (isAttending === 'waitinglist') {
        AttendButton = (
            <IconButton
                variant="primary"
                size="large"
                tooltip={t('entity-event.in-the-queue')}
                aria-label={t('entity-event.exit-queue')}
                onClick={toggleLeaveQueueModal}
                tooltipProps={{ side: 'left' }}
            >
                <CalendarWaitingIcon />
            </IconButton>
        )
    } else if (rsvp && !isGoing && isAttending !== 'waitinglist') {
        AttendButton = (
            <>
                {alreadySignedUpInSlot ? (
                    <Tooltip
                        content={t('entity-event.already-signed-up-in-slot')}
                        side="left"
                    >
                        <div>
                            <IconButton
                                variant="tertiary"
                                size="large"
                                disabled
                            >
                                <CalendarPlusIcon />
                            </IconButton>
                        </div>
                    </Tooltip>
                ) : disabled ? (
                    <Tooltip
                        content={t('entity-event.parent-attendance-required')}
                        side="left"
                    >
                        <div>
                            <IconButton
                                variant="tertiary"
                                size="large"
                                disabled
                            >
                                <CalendarPlusIcon />
                            </IconButton>
                        </div>
                    </Tooltip>
                ) : (
                    <IconButton
                        variant="secondary"
                        size="large"
                        tooltip={t('entity-event.sign-up')}
                        onClick={signUp}
                        loading={loading}
                        tooltipProps={{ side: 'left' }}
                    >
                        <CalendarPlusIcon />
                    </IconButton>
                )}
            </>
        )
    }

    return (
        <>
            {AttendButton}

            <DropdownButton options={options}>
                <IconButton
                    variant="secondary"
                    size="large"
                    tooltip={t('global.options')}
                    aria-label={t('action.show-options')}
                    style={{ marginLeft: '-4px' }}
                >
                    <OptionsIcon />
                </IconButton>
            </DropdownButton>

            <Modal
                isVisible={showDeleteModal}
                onClose={toggleDeleteModal}
                title={t('action.delete-item')}
                size="small"
            >
                <DeleteForm
                    entity={entity}
                    refetchQueries={refetchQueries}
                    onClose={toggleDeleteModal}
                />
            </Modal>

            <JoinQueueModal
                entity={entity}
                isVisible={showJoinQueueModal}
                handleSubmit={joinQueue}
                onClose={toggleJoinQueueModal}
                loading={loading}
            />
            <LeaveQueueModal
                entity={entity}
                isVisible={showLeaveQueueModal}
                handleSubmit={handleSubmit}
                onClose={toggleLeaveQueueModal}
                loading={loading}
            />
            <LeaveGuestListModal
                entity={entity}
                isVisible={showLeaveGuestListModal}
                state="reject"
                onSubmit={handleSubmit}
                onClose={toggleShowLeaveGuestListModal}
                loading={loading}
            />
            <CustomFormModal
                title={form?.title}
                formFields={form?.formFields}
                isVisible={showAttendForm}
                onSubmit={(formResponse) =>
                    handleSubmit({
                        state: isFull ? 'waitinglist' : 'accept',
                        formResponse,
                    })
                }
                onClose={() => {
                    toggleAttendForm()
                    setShowLeaveQueueModal(false)
                }}
                loading={loading}
            />
            <CopyItem
                isVisible={showCopyEventModal}
                entity={entity}
                onClose={() => setShowCopyEventModal(false)}
            />
        </>
    )
}

export default ActivityActions
